import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  SectionPanel,
  StatusPill,
  ClinicalEmptyState,
  tokens,
} from "../../../shared/clinical-design";
import { NOTE_STATUS } from "../../common/constants";
import * as cdci from "../../services/cdciAmdService";

/**
 * CDCI 4.3 — Documentación de la Nota (Pasos 3-4, corazón del módulo).
 * Renderiza la plantilla con su estructura pagelist/page/fieldlist/field de
 * AdvancedMD, prellenada con lo que el sistema ya sabe (location, paciente,
 * visita, fecha). El médico solo completa los campos clínicos.
 * Fuente futura: GET api/amd/notes/templates · POST api/amd/notes · PUT api/amd/notes/{id}
 */
export default function NoteTab({ appointment, note, onCreate, onNoteSaved, readOnly }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [creating, setCreating] = useState(false);
  const [fields, setFields] = useState(note?.fields || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    cdci.getNoteTemplates().then((tpls) => {
      setTemplates(tpls);
      // Sugerencia: la plantilla cuyo appliesTo coincide con el tipo de cita
      if (appointment) {
        const suggested = tpls.find((t) => t.appliesTo.includes(appointment.typeKey));
        setSelectedTemplate(suggested?.templateName || tpls[0]?.templateName || "");
      }
    });
  }, [appointment]);

  useEffect(() => {
    setFields(note?.fields || {});
  }, [note]);

  const template = useMemo(
    () => templates.find((t) => t.templateName === note?.templateName),
    [templates, note]
  );

  const handleCreate = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    try {
      await onCreate(selectedTemplate);
    } finally {
      setCreating(false);
    }
  };

  const handleField = (fieldname, value) => {
    setFields((prev) => ({ ...prev, [fieldname]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await cdci.updateNote(appointment.appointmentId, fields);
      onNoteSaved(updated);
      setDirty(false);
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  // --- Sin nota: selección de plantilla --------------------------------------
  if (!note) {
    return (
      <SectionPanel title="Start the encounter note">
        <Typography sx={{ fontSize: 13, color: tokens.ink2, mb: 1.5 }}>
          The note will be created pre-filled with the location, patient, visit and date —
          you only document the clinical content. The suggested template matches the
          appointment type.
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Select
            size="small"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            sx={{ minWidth: 280, bgcolor: tokens.surface }}
          >
            {templates.map((t) => (
              <MenuItem key={t.templateName} value={t.templateName}>
                {t.templateName}
                {appointment && t.appliesTo.includes(appointment.typeKey) ? "  (suggested)" : ""}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            startIcon={<PostAddIcon />}
            disabled={!selectedTemplate || creating}
            onClick={handleCreate}
            sx={{ bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 }, textTransform: "none", fontWeight: 600 }}
          >
            {creating ? "Creating note…" : "Create note"}
          </Button>
        </Stack>
      </SectionPanel>
    );
  }

  if (!template) {
    return <ClinicalEmptyState message="Loading template structure…" />;
  }

  const status = NOTE_STATUS[note.status] || NOTE_STATUS.DRAFT;

  // --- Editor de la nota -------------------------------------------------------
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>
          {note.templateName}
        </Typography>
        <StatusPill tone={status.tone} label={status.label} dense />
        {savedAt && !dirty && (
          <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>
            Saved {savedAt.toLocaleTimeString()}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{ textTransform: "none", borderColor: tokens.primary, color: tokens.primary }}
          >
            {saving ? "Saving…" : "Save progress"}
          </Button>
        )}
      </Stack>

      <Grid container spacing={1.5}>
        {template.pages.map((page) => (
          <Grid size={{ xs: 12, lg: 6 }} key={page.pageName}>
            <SectionPanel title={page.pageName} sx={{ height: "100%" }}>
              <Grid container spacing={1.2}>
                {page.fields.map((f) => {
                  const value = fields[f.fieldname] ?? "";
                  if (f.type === "readonly") {
                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={f.fieldname}>
                        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          {f.label}
                          <Box component="span" sx={{ ml: 0.7, color: tokens.info, fontSize: 10.5, textTransform: "none" }}>
                            prefilled
                          </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.ink }}>
                          {value || "—"}
                        </Typography>
                      </Grid>
                    );
                  }
                  if (f.type === "select") {
                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={f.fieldname}>
                        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mb: 0.3 }}>
                          {f.label}{f.required ? " *" : ""}
                        </Typography>
                        <Select
                          size="small"
                          fullWidth
                          value={value}
                          disabled={readOnly}
                          onChange={(e) => handleField(f.fieldname, e.target.value)}
                          displayEmpty
                          sx={{ bgcolor: tokens.surface, fontSize: 13 }}
                        >
                          <MenuItem value=""><em>—</em></MenuItem>
                          {f.options.map((o) => (
                            <MenuItem key={o} value={o}>{o}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    );
                  }
                  const isArea = f.type === "textarea";
                  return (
                    <Grid size={{ xs: 12, sm: isArea ? 12 : 6 }} key={f.fieldname}>
                      <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mb: 0.3 }}>
                        {f.label}{f.unit ? ` (${f.unit})` : ""}{f.required ? " *" : ""}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type={f.type === "number" ? "number" : "text"}
                        multiline={isArea}
                        minRows={isArea ? 3 : undefined}
                        value={value}
                        disabled={readOnly}
                        onChange={(e) => handleField(f.fieldname, e.target.value)}
                        sx={{ bgcolor: tokens.surface, "& .MuiInputBase-input": { fontSize: 13 } }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </SectionPanel>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
